import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Water as WaterIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

interface WaterProfile {
  id: string;
  name: string;
  description?: string;
  ph: number;
  ec: number;
  createdAt: string;
  elements: {
    elementSymbol: string;
    concentration: number;
  }[];
}

interface WaterProfileSelectorProps {
  onProfileSelect: (profile: WaterProfile | null) => void;
  onCreateNew: () => void;
  selectedProfileId?: string;
}

const WaterProfileSelector: React.FC<WaterProfileSelectorProps> = ({
  onProfileSelect,
  onCreateNew,
  selectedProfileId
}) => {
  const [selectionMode, setSelectionMode] = useState<'existing' | 'new'>('existing');
  const [waterProfiles, setWaterProfiles] = useState<WaterProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProfile, setExpandedProfile] = useState<string | null>(null);

  // Fetch water profiles from database
  useEffect(() => {
    fetchWaterProfiles();
  }, []);

  const fetchWaterProfiles = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/water-profiles?includeElements=true');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const profiles = await response.json();
      setWaterProfiles(profiles);

      // If no profile is selected and we have profiles, auto-select the first one
      if (!selectedProfileId && profiles.length > 0) {
        handleProfileSelect(profiles[0]);
      }
    } catch (error) {
      console.error('Error fetching water profiles:', error);
      setError(`Failed to load water profiles from database: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const mode = event.target.value as 'existing' | 'new';
    setSelectionMode(mode);
    
    if (mode === 'new') {
      onProfileSelect(null);
      onCreateNew();
    }
  };

  const handleProfileSelect = (profile: WaterProfile) => {
    onProfileSelect(profile);
  };

  const filteredProfiles = waterProfiles.filter(profile =>
    profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (profile.description && profile.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleProfileExpansion = (profileId: string) => {
    setExpandedProfile(expandedProfile === profileId ? null : profileId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getElementCount = (profile: WaterProfile) => {
    return profile.elements?.length || 0;
  };

  const getQualityIndicator = (profile: WaterProfile) => {
    const elementCount = getElementCount(profile);
    if (elementCount === 0) return { color: 'default' as const, label: 'No Elements' };
    if (elementCount < 3) return { color: 'warning' as const, label: 'Basic' };
    if (elementCount < 6) return { color: 'info' as const, label: 'Standard' };
    return { color: 'success' as const, label: 'Comprehensive' };
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Water Profile Selection
        </Typography>
        
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Choose how to proceed:</FormLabel>
          <RadioGroup
            value={selectionMode}
            onChange={handleSelectionModeChange}
            row
          >
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label="Use Existing Water Profile"
            />
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="Create New Water Profile"
            />
          </RadioGroup>
        </FormControl>

        {selectionMode === 'existing' && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading water profiles...
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                <Button onClick={fetchWaterProfiles} sx={{ ml: 2 }}>
                  Retry
                </Button>
              </Alert>
            ) : waterProfiles.length === 0 ? (
              <Alert severity="info">
                No water profiles found in database. Create a new profile to get started.
              </Alert>
            ) : (
              <Box>
                {/* Search Filter */}
                <TextField
                  fullWidth
                  placeholder="Search water profiles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Water Profiles Table */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Select</TableCell>
                        <TableCell>Profile Name</TableCell>
                        <TableCell align="center">pH</TableCell>
                        <TableCell align="center">EC (mS/cm)</TableCell>
                        <TableCell align="center">Elements</TableCell>
                        <TableCell align="center">Quality</TableCell>
                        <TableCell align="center">Created</TableCell>
                        <TableCell align="center">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredProfiles.map((profile) => {
                        const qualityIndicator = getQualityIndicator(profile);
                        const isSelected = selectedProfileId === profile.id;
                        const isExpanded = expandedProfile === profile.id;
                        
                        return (
                          <React.Fragment key={profile.id}>
                            <TableRow
                              hover
                              selected={isSelected}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleProfileSelect(profile)}
                            >
                              <TableCell>
                                <Radio
                                  checked={isSelected}
                                  onChange={() => handleProfileSelect(profile)}
                                  value={profile.id}
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {profile.name}
                                  </Typography>
                                  {profile.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {profile.description}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">{profile.ph.toFixed(1)}</TableCell>
                              <TableCell align="center">{profile.ec.toFixed(2)}</TableCell>
                              <TableCell align="center">{getElementCount(profile)}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={qualityIndicator.label}
                                  color={qualityIndicator.color}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="caption">
                                  {formatDate(profile.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleProfileExpansion(profile.id);
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            
                            {/* Expanded Details Row */}
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Element Composition:
                                    </Typography>
                                    {profile.elements && profile.elements.length > 0 ? (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {profile.elements.map((element, index) => (
                                          <Chip
                                            key={index}
                                            label={`${element.elementSymbol}: ${element.concentration} ppm`}
                                            variant="outlined"
                                            size="small"
                                          />
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No background elements defined
                                      </Typography>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredProfiles.length === 0 && searchTerm && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No water profiles match your search criteria.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {selectionMode === 'new' && (
          <Alert severity="info" icon={<WaterIcon />}>
            <Typography variant="body2">
              You've chosen to create a new water profile. The input form will be displayed below 
              where you can enter your water quality parameters and save them to the database.
            </Typography>
          </Alert>
        )}

        {/* Selection Summary */}
        {selectedProfileId && selectionMode === 'existing' && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckIcon />}>
            <Typography variant="body2">
              <strong>Selected Profile:</strong> {waterProfiles.find(p => p.id === selectedProfileId)?.name}
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default WaterProfileSelector;
